'use client';

import { motion } from 'framer-motion';
import { cn } from '@/lib/cn';

interface FeatureCardProps {
  title: string;
  description: string;
  className?: string;
  children: React.ReactNode;
}

function FeatureCard({ title, description, className, children }: FeatureCardProps) {
  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.2 }}
      className={cn(
        "rounded-2xl p-6 border border-[var(--color-border-subtle)]",
        "overflow-hidden",
        className
      )}
      style={{ backgroundColor: 'var(--color-surface)' }}
    >
      <h3 className="text-lg font-semibold text-[var(--color-text)] mb-1">{title}</h3>
      <p className="text-sm mb-4" style={{ color: 'var(--color-text-secondary)' }}>{description}</p>
      <div className="mt-2">{children}</div>
    </motion.div>
  );
}

/* Static treemap mockup */
function HeatmapMockup() {
  return (
    <div className="grid grid-cols-6 grid-rows-3 gap-1 h-44 rounded-lg overflow-hidden">
      <div className="col-span-3 row-span-2 rounded-lg flex flex-col items-center justify-center text-white" style={{ backgroundColor: 'rgba(34,197,94,0.7)' }}>
        <span className="text-sm font-bold">AAPL</span>
        <span className="text-[10px] opacity-80">+23.4%</span>
      </div>
      <div className="col-span-2 row-span-2 rounded-lg flex flex-col items-center justify-center text-white" style={{ backgroundColor: 'rgba(34,197,94,0.55)' }}>
        <span className="text-xs font-bold">MSFT</span>
        <span className="text-[10px] opacity-80">+18.1%</span>
      </div>
      <div className="col-span-1 row-span-1 rounded-lg flex flex-col items-center justify-center text-white" style={{ backgroundColor: 'rgba(34,197,94,0.4)' }}>
        <span className="text-[10px] font-bold">GOOGL</span>
        <span className="text-[9px] opacity-80">+5.8%</span>
      </div>
      <div className="col-span-1 row-span-1 rounded-lg flex flex-col items-center justify-center text-white" style={{ backgroundColor: 'rgba(34,197,94,0.65)' }}>
        <span className="text-[10px] font-bold">NVDA</span>
        <span className="text-[9px] opacity-80">+18.7%</span>
      </div>
      <div className="col-span-2 row-span-1 rounded-lg flex flex-col items-center justify-center text-white" style={{ backgroundColor: 'rgba(239,68,68,0.6)' }}>
        <span className="text-[10px] font-bold">TSLA</span>
        <span className="text-[9px] opacity-80">-4.2%</span>
      </div>
      <div className="col-span-2 row-span-1 rounded-lg flex flex-col items-center justify-center text-white" style={{ backgroundColor: 'rgba(34,197,94,0.45)' }}>
        <span className="text-[10px] font-bold">VOO</span>
        <span className="text-[9px] opacity-80">+8.3%</span>
      </div>
      <div className="col-span-2 row-span-1 rounded-lg flex flex-col items-center justify-center text-white" style={{ backgroundColor: 'rgba(239,68,68,0.45)' }}>
        <span className="text-[10px] font-bold">QQQ</span>
        <span className="text-[9px] opacity-80">-1.6%</span>
      </div>
    </div>
  );
}

/* Static stock card + lots mockup */
function StockCardMockup() {
  return (
    <div className="space-y-3">
      <div className="rounded-xl p-4 border border-[var(--color-border-subtle)]" style={{ backgroundColor: 'var(--color-bg)' }}>
        <div className="flex items-center justify-between mb-2">
          <div>
            <span className="text-sm font-bold text-[var(--color-text)]">AAPL</span>
            <span className="text-[10px] ml-2" style={{ color: 'var(--color-text-muted)' }}>Apple Inc.</span>
          </div>
          <span className="text-sm font-bold text-green-400">+23.4%</span>
        </div>
        <div className="text-xs space-y-1" style={{ color: 'var(--color-text-secondary)' }}>
          <div className="flex justify-between">
            <span>Lot 1 — 50 shares @ $142.00</span>
            <span className="text-green-400">+$2,825</span>
          </div>
          <div className="flex justify-between">
            <span>Lot 2 — 25 shares @ $168.50</span>
            <span className="text-green-400">+$750</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* Static pie chart mockup (SVG) */
function PieChartMockup() {
  const segments = [
    { label: 'Tech', pct: 45, color: '#3b82f6', offset: 0 },
    { label: 'Healthcare', pct: 20, color: '#22c55e', offset: 45 },
    { label: 'Finance', pct: 15, color: '#a855f7', offset: 65 },
    { label: 'Energy', pct: 12, color: '#f59e0b', offset: 80 },
    { label: 'Other', pct: 8, color: '#64748b', offset: 92 },
  ];

  return (
    <div className="flex items-center gap-4">
      <svg viewBox="0 0 36 36" className="w-28 h-28 flex-shrink-0">
        {segments.map((seg) => (
          <circle
            key={seg.label}
            r="15.9155"
            cx="18"
            cy="18"
            fill="transparent"
            stroke={seg.color}
            strokeWidth="3.5"
            strokeDasharray={`${seg.pct} ${100 - seg.pct}`}
            strokeDashoffset={`${-seg.offset}`}
            className="origin-center -rotate-90"
            style={{ transformOrigin: '18px 18px' }}
          />
        ))}
      </svg>
      <div className="space-y-1.5">
        {segments.map((seg) => (
          <div key={seg.label} className="flex items-center gap-2 text-xs">
            <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: seg.color }} />
            <span style={{ color: 'var(--color-text-secondary)' }}>{seg.label}</span>
            <span className="font-medium text-[var(--color-text)]">{seg.pct}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* Static AI research mockup */
function AIResearchMockup() {
  return (
    <div className="rounded-xl p-4 border border-[var(--color-border-subtle)]" style={{ backgroundColor: 'var(--color-bg)' }}>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-sm font-bold text-[var(--color-text)]">NVDA Research</span>
        <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-green-500/20 text-green-400">
          BUY
        </span>
      </div>
      <ul className="space-y-1.5 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
        <li className="flex items-start gap-1.5">
          <span className="mt-1 w-1 h-1 rounded-full bg-[var(--color-accent)] flex-shrink-0" />
          Strong data center revenue growth driven by AI demand
        </li>
        <li className="flex items-start gap-1.5">
          <span className="mt-1 w-1 h-1 rounded-full bg-[var(--color-accent)] flex-shrink-0" />
          Next-gen GPU architecture maintains competitive moat
        </li>
        <li className="flex items-start gap-1.5">
          <span className="mt-1 w-1 h-1 rounded-full bg-[var(--color-accent)] flex-shrink-0" />
          Forward P/E suggests premium but justified by growth
        </li>
      </ul>
    </div>
  );
}

export function FeaturesBentoGrid() {
  return (
    <section id="features" className="py-20">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-[var(--color-text)]">
            Everything You Need
          </h2>
          <p className="mt-3 text-lg max-w-2xl mx-auto" style={{ color: 'var(--color-text-secondary)' }}>
            Powerful tools to visualize, analyze, and optimize your investment portfolio.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <FeatureCard
            title="Portfolio Heatmap"
            description="See your entire portfolio at a glance with an interactive treemap visualization."
            className="md:col-span-4"
          >
            <HeatmapMockup />
          </FeatureCard>

          <FeatureCard
            title="Stock Cards & Lots"
            description="Track individual purchase lots with per-lot P/L breakdown."
            className="md:col-span-2"
          >
            <StockCardMockup />
          </FeatureCard>

          <FeatureCard
            title="ETF Allocations"
            description="Understand your sector exposure across all holdings."
            className="md:col-span-3"
          >
            <PieChartMockup />
          </FeatureCard>

          <FeatureCard
            title="AI Research"
            description="Get AI-powered analysis and ratings for any stock in your portfolio."
            className="md:col-span-3"
          >
            <AIResearchMockup />
          </FeatureCard>
        </div>
      </div>
    </section>
  );
}
